import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAccessToken } from '../auth/use-access-token.js';
import { useBookings } from '../api/use-bookings.js';
import { cancelLessonAsParent } from '../api/cancel-lesson.js';
import { firstName } from '../api/first-name.js';
import { hasStarted } from '../api/has-started.js';
import { athleteLessonLink } from '../api/lesson-link.js';
import { WaitNote } from '../components/wait-note.js';
import { BusyLabel } from '../components/busy-label.js';

/**
 * A parent cancels one child's lesson.
 *
 * ONE CONFIRMATION, AND IT IS THIS SCREEN. The back chevron keeps the lesson;
 * the solid warn button is the only act here. It says what happens — nothing
 * is charged, and the coach will see it — because that is what a parent needs
 * to know before pressing it.
 *
 * Ruled 10 October: the other family in a group is not told, so nothing here
 * mentions them.
 */
export function ParentCancelRoute() {
  const { bookingId, lessonId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const getToken = useAccessToken();
  const { state } = useBookings();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  if (state.status === 'loading') return <WaitNote className="empty">Checking…</WaitNote>;
  if (state.status === 'failed') return <p className="empty">{state.message}</p>;

  const booking = state.lessons.find((candidate) => candidate.booking_id === bookingId);
  const attendee = booking?.attendees.find((candidate) => candidate.lesson_id === lessonId);
  // Back to the child's own page — where Cancel was pressed, and where the
  // cancellation is read afterwards — keeping the way in, if it was a person.
  const back =
    booking && attendee?.lesson_id
      ? athleteLessonLink(booking.booking_id, attendee.lesson_id, params.get('from') === 'person')
      : '/lessons';

  if (
    !booking ||
    !attendee ||
    !attendee.lesson_id ||
    attendee.cancellation !== null ||
    attendee.lesson_closed ||
    booking.booking_status !== 'confirmed' ||
    hasStarted(booking.slot_start)
  ) {
    return (
      <section className="panel">
        <h2>Nothing to cancel</h2>
        <p className="empty">
          This lesson is not on your account, or it can no longer be cancelled.
        </p>
        <Link className="primary" to={back}>
          Back
        </Link>
      </section>
    );
  }

  const childLessonId = attendee.lesson_id;
  const coach = booking.coach_name ? `Coach ${firstName(booking.coach_name)}` : 'Your coach';

  async function confirm() {
    setBusy(true);
    setProblem(null);
    try {
      await cancelLessonAsParent(getToken, childLessonId);
      navigate(back, { replace: true });
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : String(cause));
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <Link className="back" to={back} aria-label="Keep the lesson">
          ‹
        </Link>
        <div>
          <h2>Cancel {firstName(attendee.name)}'s lesson</h2>
          <span className="detail">
            {[when(booking.slot_start), booking.product_name].filter(Boolean).join(' · ')}
          </span>
        </div>
      </div>

      <div className="fact">
        <span className="label">Athlete</span>
        <span className="value">{attendee.name}</span>
      </div>
      <div className="fact">
        <span className="label">Coach</span>
        <span className="value">{booking.coach_name || '—'}</span>
      </div>

      <p className="statement">Nothing is charged. {coach} will see that you cancelled.</p>

      {problem ? <p className="warn-note">{problem}</p> : null}

      <button type="button" className="warn-fill" disabled={busy} aria-busy={busy} onClick={() => void confirm()}>
        <BusyLabel busy={busy} working="Cancelling…">
          Cancel lesson
        </BusyLabel>
      </button>
    </>
  );
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}
